/**
 * The built-in rule: which elements a room's measurements treat as part of
 * the house. An element is built in by its type's own rule
 * (`freeElementTypes`), and a movable appliance also counts when built-ins
 * hold it on two opposite sides, the way a range or dishwasher sits in a run
 * of cabinets: then the floor and wall behind it are never finished.
 */
import { freeElementType, isAppliance } from './freeElementTypes';
import { CONTACT_TOLERANCE, type ElementSide, facing, freeElementSides } from './geometry';
import { type FloorPlanFreeElement, freeElementBase } from './schemas';

const builtInByType = (element: FloorPlanFreeElement): boolean => freeElementType(element).builtIn(element);

/** How much of two sides overlap when they are parallel and within an inch of each other; 0 when they don't touch. */
const touching = (a: ElementSide, b: ElementSide): number => {
  const face = facing(a, b);
  return face && face.distance <= CONTACT_TOLERANCE ? face.overlap : 0;
};

/** Whether built-ins hold the element on two opposite sides. */
const heldInRun = (element: FloorPlanFreeElement, elements: readonly FloorPlanFreeElement[]): boolean => {
  const neighbors = elements.filter(other => other !== element && builtInByType(other));
  const [back, right, front, left] = freeElementSides(element).map(side =>
    neighbors.some(other => freeElementSides(other).some(otherSide => touching(side, otherSide) > 0))
  );
  return (back && front) || (left && right);
};

/** Whether an element counts as built in for measuring: by its type's rule, or as an appliance held in a run of built-ins. */
export const isMeasuredAsBuiltIn = (element: FloorPlanFreeElement, elements: readonly FloorPlanFreeElement[]): boolean =>
  builtInByType(element) || (isAppliance(element) && heldInRun(element, elements));

/** Whether the rule measures the element as a fixture at all: flooring only retypes the floor, and an interior wall is measured as wall. */
const isFixture = (element: FloorPlanFreeElement): boolean => {
  const { floorRole } = freeElementType(element);
  return floorRole !== 'retypes' && floorRole !== 'carves';
};

/** The fixtures measured as built in: they come out of the floor they stand on and the walls they stand against. */
export const measuredBuiltIns = (elements: readonly FloorPlanFreeElement[]): FloorPlanFreeElement[] =>
  elements.filter(element => isFixture(element) && isMeasuredAsBuiltIn(element, elements));

/** Built-in elements standing on the floor: the floor under them is never finished. */
export const floorCoveringElements = (elements: readonly FloorPlanFreeElement[]): FloorPlanFreeElement[] =>
  measuredBuiltIns(elements).filter(element => freeElementBase(element) <= CONTACT_TOLERANCE);
